import { useState } from "react";
import { ChevronDown } from "lucide-react";
import AnimateIn from "../ui/AnimateIn";

/* ─── FAQ Item ─── */

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-warm-border">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between py-5 text-left text-warm-text-primary font-medium hover:text-warm-blue transition-colors"
      >
        <span className="pr-4">{question}</span>
        <ChevronDown
          className={`w-5 h-5 shrink-0 text-warm-text-tertiary transition-transform duration-300 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      <div
        className={`grid transition-[grid-template-rows] duration-300 ${
          isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <p
            className={`text-warm-text-secondary leading-relaxed ${
              isOpen ? "pb-5" : ""
            }`}
          >
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
}

const FAQ_ITEMS = [
  {
    question: "What is JuDI?",
    answer:
      "JuDI is a task manager that keeps your tasks under control. Speak them in, sync with Google Calendar, talk to an assistant, and unfinished tasks follow you until they're done. It also has a panoramic board, filters, and more.",
  },
  {
    question: "How much does JuDI cost?",
    answer:
      "1 month free, then $5 a month. Your card is saved when you start. The price includes the whole product, including voice.",
  },
  {
    question: "How does voice input work?",
    answer:
      "Press V or tap the microphone button, then speak naturally about your tasks. JuDI's AI transcribes your speech and structures it automatically — extracting dates, priorities, lists, and subtasks. You can review and edit before saving.",
  },
  {
    question: "Does JuDI work on mobile?",
    answer:
      "Yes! JuDI is a Progressive Web App (PWA) that works on any device with a browser. You can install it on your home screen for a native app experience, and it even works offline.",
  },
  {
    question: "How does Google Calendar sync work?",
    answer:
      "Connect your Google account in Settings, and JuDI creates a dedicated calendar for your tasks. Scheduled tasks sync bi-directionally — your tasks appear on your calendar, and your calendar events appear in JuDI's calendar view. Drag tasks to schedule them.",
  },
  {
    question: "Is my data secure?",
    answer:
      "Absolutely. JuDI uses Supabase with row-level security, meaning your data is isolated and accessible only to you. File attachments are stored in user-specific folders with secure access controls.",
  },
  {
    question: "How do I delete my account?",
    answer:
      "Open Settings → Account and choose Delete account. Type DELETE to confirm. This removes your tasks, files, and connected Google data from JuDI, and cancels a website plan billed through Stripe. It does not cancel Google Play or Apple. The same option is in the iPhone and Android apps under Settings. If you cannot open the app, email the address on the privacy page. The steps are also at https://www.usejudi.com/account-deletion.",
  },
];

export default function FAQSection() {
  return (
    <section id="faq" className="py-20 md:py-28 bg-warm-bg-elevated">
      <div className="max-w-3xl mx-auto px-6">
        <AnimateIn>
          <h2 className="font-display text-3xl md:text-4xl text-warm-text-primary mb-10 text-center">
            Frequently asked questions
          </h2>
        </AnimateIn>
        <AnimateIn delay={100}>
          <div className="bg-warm-bg-card border border-warm-border rounded-2xl px-6 md:px-8 divide-y divide-warm-border">
            {FAQ_ITEMS.map((item) => (
              <FAQItem key={item.question} question={item.question} answer={item.answer} />
            ))}
          </div>
        </AnimateIn>
      </div>
    </section>
  );
}
